import React, { useEffect, useState } from "react";

import "animate.css";
import StepWizard from "react-step-wizard";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { usePageTitle } from "@/atoms/page-title-atom";
import { gql } from "@apollo/client";
import { Chrome } from "@components/chrome";
import { LoadingScreen } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  useFeatureFlags,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import {
  OnboardingStepType,
  type GetSteps,
  type GetSteps_me_onboardingSteps,
  type MarkStepAsComplete,
  type MarkStepAsCompleteVariables,
  type StartStep,
  type StartStepVariables,
  type UpdateStepMetadata,
  type UpdateStepMetadataVariables,
} from "@north-beam/nb-client-side";

import { OnboardingNav as OnboardingNavV2 } from "./onboarding-nav";
import { useOnboardingStep } from "./onboarding-nav/use-onboarding-number";
import { stepIsLast, stepIsVisible, Steps } from "./steps";

import "./index.scss";

export const START_STEP = gql`
  mutation StartStep($step: OnboardingStepType!) {
    startOnboardingStep(step: $step) {
      step
      error
    }
  }
`;
export const MARK_STEP_COMPLETED = gql`
  mutation MarkStepAsComplete(
    $step: OnboardingStepType!
    $isCompleted: Boolean!
  ) {
    markStepAsComplete(step: $step, isCompleted: $isCompleted) {
      step
      error
    }
  }
`;
export const GET_STEPS = gql`
  query GetSteps {
    me {
      onboardingSteps {
        client_id
        end_time
        start_time
        step
        step_metadata
      }
    }
  }
`;
export const UPDATE_STEP_METADATA = gql`
  mutation UpdateStepMetadata($step: OnboardingStepType!, $data: JSONObject!) {
    updateOnboardingStepMetadata(step: $step, blob: $data) {
      step
      step_metadata
    }
  }
`;

export type SubNavItem = {
  label: React.FC | string;
  subStep: string;
};

export const SideNavContext = React.createContext({
  subNavItems: [] as SubNavItem[],
  setSubNavItems: (subItems: SubNavItem[]) => {},
});

const apiStepName = (stepV2Name: string): string => {
  return stepV2Name.toUpperCase().replaceAll("-", "_");
};

const Onboarding = () => {
  const { user } = useUser();

  const [, setPageTitle] = usePageTitle();

  const { loading } = useFeatureFlags();
  const onboardingSteps = Steps;
  const { data: onboardingV2Steps, loading: isOnboardingV2StepsLoading } =
    useNorthbeamQuery<GetSteps>(GET_STEPS, {
      fetchPolicy: "network-only",
    });

  const [updateStepMetadata] = useNorthbeamMutation<
    UpdateStepMetadata,
    UpdateStepMetadataVariables
  >(UPDATE_STEP_METADATA, {
    refetchQueries: [{ query: GET_STEPS }],
    doNotRaiseErrorOnFailure: true,
  });

  const [stepsMap, setStepsMap] = useState<{
    [key in OnboardingStepType]: GetSteps_me_onboardingSteps;
  }>(
    Object.fromEntries(
      Object.values(OnboardingStepType).map((type) => [type, {}]),
    ) as { [key in OnboardingStepType]: GetSteps_me_onboardingSteps },
  );

  useEffect(() => {
    if (isOnboardingV2StepsLoading || !onboardingV2Steps?.me.onboardingSteps) {
      return;
    }
    onboardingV2Steps.me.onboardingSteps.forEach(
      (step) => (stepsMap[step.step] = step),
    );
    setStepsMap({ ...stepsMap });
  }, [isOnboardingV2StepsLoading, setStepsMap, onboardingV2Steps]);

  const [startStep] = useNorthbeamMutation<StartStep, StartStepVariables>(
    START_STEP,
    {
      refetchQueries: [{ query: GET_STEPS }],
      doNotRaiseErrorOnFailure: true,
    },
  );

  const { inversetStepDict } = useOnboardingStep();

  const [subNavItems, setSubNavItems] = useState<SubNavItem[]>([]);
  const [_, setSearchParams] = useQueryParams({
    step: withDefault(StringParam, ""),
  });
  const goToNamedStep = (step: string) => setSearchParams({ step });

  const [endStep] = useNorthbeamMutation<
    MarkStepAsComplete,
    MarkStepAsCompleteVariables
  >(MARK_STEP_COMPLETED, {
    refetchQueries: [{ query: GET_STEPS }],
    doNotRaiseErrorOnFailure: true,
  });

  if (loading) {
    return <LoadingScreen />;
  }

  setPageTitle("Setup Northbeam");

  const pixelConfiguratorPredicate = (step: OnboardingStepType) => {
    if (user.isPixelConfigurationSubaccount) {
      return (
        step !== OnboardingStepType.DNS_SETUP &&
        step !== OnboardingStepType.PIXEL_PLACEMENT
      );
    }
    return true;
  };

  const canVerifyAndFinish = !Object.values(stepsMap).find(
    ({ step, end_time }) =>
      pixelConfiguratorPredicate(step) &&
      step !== OnboardingStepType.ACCOUNT_MANAGERS &&
      step !== OnboardingStepType.CURRENCY &&
      step !== OnboardingStepType.BASIC_DETAILS &&
      step !== OnboardingStepType.DOMAIN &&
      !end_time,
  );

  const unblockedFinishOnVerifyScreen = !Object.values(stepsMap).find(
    ({ step, end_time }) =>
      pixelConfiguratorPredicate(step) &&
      step !== OnboardingStepType.ACCOUNT_MANAGERS &&
      step !== OnboardingStepType.CURRENCY &&
      step !== OnboardingStepType.BASIC_DETAILS &&
      step !== OnboardingStepType.DOMAIN &&
      step !== OnboardingStepType.VERIFY &&
      !end_time,
  );

  return (
    <SideNavContext.Provider value={{ subNavItems, setSubNavItems }}>
      <Chrome onboarding>
        <StepWizard
          isLazyMount
          nav={
            <OnboardingNavV2
              onboardingV2Steps={stepsMap}
              goToNamedStep={goToNamedStep}
              canVerifyAndFinish={canVerifyAndFinish}
            />
          }
          onStepChange={async (stepInformation) => {
            setSubNavItems([]);

            const [activeStepName, previousStepName] = [
              inversetStepDict[stepInformation.activeStep].step,
              inversetStepDict[stepInformation.previousStep].step,
            ];

            if (activeStepName === "complete") {
              if (!canVerifyAndFinish) {
                setSearchParams({
                  step: Steps.INTRODUCTION,
                });
              }
              return;
            }

            const persistedStep = onboardingV2Steps?.me.onboardingSteps?.find(
              (apiStep) => apiStep.step === apiStepName(activeStepName),
            );

            if (!persistedStep) {
              setStepsMap({
                ...stepsMap,
                [apiStepName(activeStepName) as OnboardingStepType]: {
                  start_time: "now",
                },
              });
              await startStep({
                variables: {
                  step: apiStepName(activeStepName) as OnboardingStepType,
                },
              });
            }
            if (
              previousStepName === Steps.CURRENCY &&
              !persistedStep?.end_time
            ) {
              setStepsMap({
                ...stepsMap,
                [OnboardingStepType.CURRENCY]: {
                  ...stepsMap[OnboardingStepType.CURRENCY],
                  end_time: "now",
                },
              });
              await endStep({
                variables: {
                  step: apiStepName(Steps.CURRENCY) as OnboardingStepType,
                  isCompleted: true,
                },
              });
            }
            if (
              previousStepName === Steps.INTRODUCTION &&
              (!persistedStep?.start_time || !persistedStep?.end_time)
            ) {
              await startStep({
                variables: {
                  step: apiStepName(Steps.INTRODUCTION) as OnboardingStepType,
                },
              });
              await endStep({
                variables: {
                  step: apiStepName(Steps.INTRODUCTION) as OnboardingStepType,
                  isCompleted: true,
                },
              });
            }
          }}
          transitions={{
            enterRight: "",
            enterLeft: "",
            exitRight: "",
            exitLeft: "",
          }}
          className="onboarding-step-wizard"
        >
          {isOnboardingV2StepsLoading ? (
            <div>LOADING</div>
          ) : (
            onboardingSteps.data
              .filter((s: any) => stepIsVisible(s.value, user))
              .map(({ component: Component, value }: any) => {
                const step = apiStepName(value) as OnboardingStepType;
                return (
                  <Component
                    key={value}
                    stepName={value}
                    goToNamedStep={goToNamedStep}
                    isLast={stepIsLast(value, user)}
                    isMarkedComplete={!!stepsMap[step]?.end_time}
                    stepMetadata={stepsMap[step]?.step_metadata}
                    canVerifyAndFinish={canVerifyAndFinish}
                    unblockedFinishOnVerifyScreen={
                      unblockedFinishOnVerifyScreen
                    }
                    updateStepMetadata={async (data: any) => {
                      const stepData = stepsMap[step];
                      setStepsMap({
                        ...stepsMap,
                        [step]: {
                          ...stepData,
                          step_metadata: data,
                        },
                      });
                      await updateStepMetadata({
                        variables: {
                          step,
                          data: data,
                        },
                      });
                    }}
                    getStepMetadata={(stepName: string) => {
                      return stepsMap[
                        apiStepName(stepName) as OnboardingStepType
                      ]?.step_metadata;
                    }}
                    toggleStepComplete={async () => {
                      const stepData = stepsMap[step];
                      if (!stepData?.end_time) {
                        // mark complete
                        setStepsMap({
                          ...stepsMap,
                          [step]: {
                            ...stepData,
                            end_time: "now",
                          },
                        });
                      } else {
                        // mark incomplete (remove end_time entry)
                        setStepsMap({
                          ...stepsMap,
                          [apiStepName(value) as OnboardingStepType]:
                            Object.fromEntries(
                              Object.entries(stepData).filter(
                                ([key]) => key !== "end_time",
                              ),
                            ),
                        });
                      }

                      await endStep({
                        variables: {
                          step: step,
                          isCompleted: !stepData?.end_time,
                        },
                      });
                    }}
                  />
                );
              })
          )}
        </StepWizard>
        <div id="flyout-portal"></div>
      </Chrome>
    </SideNavContext.Provider>
  );
};

export default Onboarding;
